/** @format */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import * as stackActions from '../actions/stacks.actions';
import * as clipActions from '../actions/clips.actions';
import * as userActions from '../actions/user.actions';
import { AnalyticsEventName, AnalyticsService } from '@app/core/services/analytics/analytics.service';

const PAGE = '[AnalyticsEffects]';

type ActionsUnion = clipActions.ActionsUnion | stackActions.ActionsUnion | userActions.ActionsUnion;

@Injectable()
export class AnalyticsEffects {
  private actions$ = inject<Actions<ActionsUnion>>(Actions);
  private analyticsService = inject(AnalyticsService);

  stackWatch$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(stackActions.selectIdPlay.type),
        tap((action) => {
          const { type, ...props } = action;
          this.analyticsService.recordEvent(AnalyticsEventName.STACKS_WATCH, props);
        })
      ),
    { dispatch: false }
  );

  stackEdit$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(stackActions.selectIdEdit.type),
        tap((action) => {
          const { type, ...props } = action;
          this.analyticsService.recordEvent(AnalyticsEventName.STACKS_EDIT, props);
        })
      ),
    { dispatch: false }
  );

  stackWatchComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(stackActions.watchComplete.type),
        tap((action) => {
          const { type, ...props } = action;
          this.analyticsService.recordEvent(AnalyticsEventName.STACKS_WATCH_COMPLETE, props);
        })
      ),
    { dispatch: false }
  );

  clipPlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(clipActions.play.type),
        tap((action) => {
          const { type, ...props } = action;
          if (props && props.clip && props.clip.projectId) {
            const payload = {
              projectId: props.clip.projectId,
              clipId: props.clip.id,
            };
            this.analyticsService.recordEvent(AnalyticsEventName.CLIPS_PLAY, payload);
          } else {
            // Fix: Sentry-X8 projectId of null
            // Saw again in -10E after "UpdateService clearing store state and reloading app" (not a surprise that store is empty)
            console.log(`Why are we missing the clip prop here?`, action);
          }
        })
      ),
    { dispatch: false }
  );

  stackShare$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(stackActions.share.type),
        tap((action) => {
          const { type, ...props } = action;
          /**
           * T4.14 FIX 2026-10-20: `props.response.shareUrl` was read with no
           * guard at all, while clipPlay$ above guards `props.clip.projectId`
           * - the same inconsistency that produced the StackPopoverActions
           * defect found during the T4.1 triage. A `share` action dispatched
           * without a `response` threw inside this tap, and since NO effect in
           * this file has a catchError, the only thing keeping analytics alive
           * afterwards was NgRx's default resubscribe-on-error.
           *
           * Now it records what it has rather than throwing. A share event
           * with no response detail is still worth counting; losing the whole
           * stream is not.
           */
          if (!props || !props.response) {
            console.warn(`${PAGE} stackShare$ missing response on share action`, action);
            this.analyticsService.recordEvent(AnalyticsEventName.STACKS_SHARE, {
              projectId: props?.projectId,
              stackId: props?.stackId,
            });
            return;
          }
          const payload = {
            projectId: props.projectId,
            stackId: props.stackId,
            shareUrl: props.response.shareUrl,
            method: props.response.method,
            itemType: props.response.itemType,
          };
          this.analyticsService.recordEvent(AnalyticsEventName.STACKS_SHARE, payload);
        })
      ),
    { dispatch: false }
  );

  stackPlayIndex$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(stackActions.playCurrentIndex.type),
        tap((action) => {
          const { type, ...props } = action;
          this.analyticsService.recordEvent(AnalyticsEventName.STACKS_PLAY_INDEX, props);
        })
      ),
    { dispatch: false }
  );
}
